import './PublicOrderRequestPage.scss';

import useAuth from 'common/useAuth';
import { ErrorAlert, LoadingIndicator } from 'components';
import { PublicPage } from 'components/Layout';
import { PUBLIC_ERROR_KEY } from 'helpers/errorHandling';
import { getHouseholdFromOrderRequesFormType } from 'helpers/orderRequestHelpers';
import { getLanguageCode, useCustomTranslation } from 'helpers/translationHelper';
import type { SUPPORTED_LANGUAGES_TYPE } from 'i18n';
import type { CustomerKind } from 'pages/customers/models';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import PublicOrderRequestForm from './components/PublicOrderRequestForm';
import PublicOrderRequestHeader from './components/PublicOrderRequestHeader';
import { useGetPublicOrderRequestQuery } from './queries';

type RouteParams = 'tenantCode' | 'publicFormCode';

const PublicOrderRequestPage = () => {
  const { t } = useCustomTranslation();
  const params = useParams<RouteParams>();

  const { setLanguage } = useAuth();

  const [expiredError, setExpiredError] = useState('');
  const [defaultCustomerKind, setDefaultCustomerKind] = useState<CustomerKind>();
  const [successfullySubmited, setSuccessfullySubmitted] = useState(false);

  const { data, isLoading } = useGetPublicOrderRequestQuery(
    params.tenantCode || '',
    params.publicFormCode || '',
    setExpiredError
  );

  useEffect(() => {
    data?.company.languageId &&
      !getLanguageCode() &&
      setLanguage(data.company.languageId.toLowerCase() as SUPPORTED_LANGUAGES_TYPE);
    data?.company &&
      data.company.orderRequestFormType !== 'CompanyAndHousehold' &&
      setDefaultCustomerKind(
        getHouseholdFromOrderRequesFormType(data.company.orderRequestFormType)
      );
  }, [data?.company, setLanguage]);

  const showError = expiredError || !params.publicFormCode || !params.tenantCode;

  if (isLoading) return <LoadingIndicator />;

  if (showError) {
    return (
      <PublicPage
        title={t('orderRequests.publicRequestHeading')}
        className="PublicOrderRequestPage"
      >
        <ErrorAlert errorCode={PUBLIC_ERROR_KEY} newTranslate />
      </PublicPage>
    );
  }

  return (
    <PublicPage title={t('orderRequests.publicRequestHeading')} className="PublicOrderRequestPage">
      <PublicOrderRequestHeader
        companyName={data?.company.name}
        logoUrl={data?.company.logoUrl}
        orderRequestInfoPageContent={data?.company.orderRequestInfoPageContent}
        setDefaultCustomerKind={setDefaultCustomerKind}
        defaultCustomerKind={defaultCustomerKind}
        successfullySubmitted={successfullySubmited}
        setSuccessfullySubmitted={() => setSuccessfullySubmitted(false)}
      />
      {defaultCustomerKind && !successfullySubmited && (
        <PublicOrderRequestForm
          defaultKind={defaultCustomerKind}
          countryId={data?.company.countryId}
          categories={data?.categories || []}
          tenantCode={params.tenantCode || ''}
          publicFormCode={params.publicFormCode || ''}
          onSuccess={() => setSuccessfullySubmitted(true)}
          orderRequestFormRequestedDateEnabled={data?.company.orderRequestFormRequestedDateEnabled}
          requestedDateNote={data?.company.requestedDateNote}
          orderRequestFormServiceObjectInfoEnabled={
            data?.company.orderRequestFormServiceObjectInfoEnabled
          }
          hideKindSwitch={data?.company.orderRequestFormType !== 'CompanyAndHousehold'}
          addressSearchProvider={data?.company.addressSearchProvider || 'Google'}
        />
      )}
    </PublicPage>
  );
};

export default PublicOrderRequestPage;
